import React, { useState } from 'react';
import Button from '$/components/base/Button';
import Modal from '$/components/base/Modal';
import IconFont from '$/components/IconFont';
import { useLanguageStore } from '$/store/languageStore';
import useAIToolsCanvasStore, { undo, redo } from '@/store/useAIToolsCanvasStore';
import { Tooltip } from 'antd';
import { useShallow } from 'zustand/react/shallow';
import style from './index.module.scss';

interface CanvasHeaderBarProps {
  /** 点击重置确认后回调 */
  onReset?: () => void;
  /** 当前单图任务是否在处理中 */
  singleEraserLoading?: boolean;
  /** 当前单图结果图 */
  singleResultImage?: string;
}

const iconToolbarButtonStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  minWidth: 32,
  padding: 0,
};

/**
 * 画布顶部操作栏
 * 撤销 | 重做 | 重置
 */
export default function CanvasHeaderBar({
  onReset,
  singleEraserLoading = false,
  singleResultImage = '',
}: CanvasHeaderBarProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const { undoEnabled, redoEnabled } = useAIToolsCanvasStore(useShallow((state) => ({
    undoEnabled: state.undoEnabled,
    redoEnabled: state.redoEnabled,
  })));
  const [showResetModal, setShowResetModal] = useState(false);
  const undoDisabled = !undoEnabled || singleEraserLoading;
  const redoDisabled = !redoEnabled || singleEraserLoading;
  /** 有结果图、或可撤销/可重做时均可重置；loading 时禁用 */
  const resetDisabled = singleEraserLoading || (!singleResultImage && !undoEnabled && !redoEnabled);

  const handleReset = () => {
    setShowResetModal(false);
    onReset?.();
  };

  return (
    <>
      <div className={style.canvas_header_bar}>
        {/* 撤销 */}
        <Tooltip
          title={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_studio_tool_undo)}
          overlayClassName={style.tooltip_overlay}
        >
          <Button
            type="transparent"
            size="small"
            disabled={undoDisabled}
            buttonStyle={iconToolbarButtonStyle}
            onClick={undo}
          >
            <IconFont name="arrowUndoV2Bold" fontSize={20} color="var(--content-primary)" />
          </Button>
        </Tooltip>
        <div className={style.divider} />
        {/* 重做 */}
        <Tooltip
          title={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_studio_tool_redo)}
          overlayClassName={style.tooltip_overlay}
        >
          <Button
            type="transparent"
            size="small"
            disabled={redoDisabled}
            buttonStyle={iconToolbarButtonStyle}
            onClick={redo}
          >
            <IconFont name="arrowRedoV2Bold" fontSize={20} color="var(--content-buttonSecondary)" />
          </Button>
        </Tooltip>
        {/* 重置 */}
        <Tooltip title={languageStore.smartcatTranslationText((v) => v.reset_tools)} overlayClassName={style.tooltip_overlay}>
          <Button
            type="transparent"
            size="small"
            disabled={resetDisabled}
            buttonStyle={{ height: 32, padding: '0 12px', minWidth: 'auto' }}
            onClick={() => setShowResetModal(true)}
          >
            <IconFont name="arrowRefreshBold" fontSize={20} color="var(--content-primary)" style={{ marginRight: 4 }} />
          </Button>
        </Tooltip>
      </div>

      {/* 重置确认弹窗 */}
      <Modal
        isModalOpen={showResetModal}
        cancel={() => setShowResetModal(false)}
        close={() => setShowResetModal(false)}
        confirm={() => handleReset()}
        type="warning"
      >
        <div className={style.reset_modal_content}>
          <div className={style.reset_modal_title}>
            {languageStore.smartcatTranslationText((v) => v.confirm_the_reset_)}
          </div>
          <div className={style.reset_modal_desc}>
            {languageStore.smartcatTranslationText(
              (v) => v.resetting_will_clear_all_current_operations_and_restore_them_to_their_original_drawing,
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
